import { useEffect, useMemo, useState } from "react";
import { Card, Col, Row, Table, Typography, Grid, Button } from "antd";
import { Icon } from "@iconify/react";
import { format } from "date-fns";
import pallete from "../utils/pallete";
import UpdatedByCell from "../components/UpdatedByCell";
import StatusTag from "../components/StatusTag";
import { dashboardService } from "../services/dashboard.service";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import RenderIf from "../components/RenderIf";
import { useNavigate } from "react-router";
import useTitleStore from "../store/titleStore";
import VocationalDashboardIcon from "@/assets/icons/vocationalDashboardIcon.svg?react";
import StateVocationalDashboardIcon from "@/assets/icons/stateVocationalDashboardIcon.svg?react";
import CPMIDashboardIcon from "@/assets/icons/cpmiDashboardIcon.svg?react";
import GovernmentDashboardIcon from "@/assets/icons/governmentDashboardIcon.svg?react";
import EmployerDashboardIcon from "@/assets/icons/employerDashboardIcon.svg?react";
import TrainingTypeTag from "../components/TrainingTypeTag";
import { COST_SOURCE_LABELS } from "../utils/trainingCatalog.constants";
import { useLocalStorage } from "../hooks/useLocalStorage";

export default function Dashboard() {
  const [page, setPage] = useState(1);
  // const { user } = useAuth();
  const [user] = useLocalStorage("siskop2mi_user", null);
  const directorate = user?.directorate;
  const pageSize = 10;
  const navigate = useNavigate();
  const { setTitle } = useTitleStore();

  // User Directorate
  const isSuperAdmin = user?.position == "Superadmin";
  const isVocationalDirectorate = directorate == "Direktorat Vokasi";
  const isCapacityDirectorate =
    user?.directorate === "Direktorat Peningkatan Kapasitas";

  const { data: dashboardData } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => dashboardService.fetchData(),
    placeholderData: keepPreviousData,
  });

  const { data: trainingCatalogData } = useQuery({
    queryKey: ["training-catalog-data"],
    queryFn: () => dashboardService.fetchTrainingCatalogData(),
    placeholderData: keepPreviousData,
  });

  const data = isVocationalDirectorate ? dashboardData : trainingCatalogData;

  const filteredVocationalData = useMemo(() => {
    if (!data?.data?.latest) return [];

    if (
      directorate === "Direktorat Peningkatan Kapasitas" ||
      directorate == "*"
    ) {
      return data.data.latest;
    }

    if (isVocationalDirectorate) {
      return data.data.latest;
    }

    return [];
  }, [data, directorate]);

  const breakpoint = Grid.useBreakpoint();

  useEffect(() => {
    setTitle("Dashboard");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = useMemo(
    () => [
      {
        title: "No.",
        dataIndex: "id",
        width: 50,
        render: (text, record, index) => index + 1 + (page - 1) * pageSize,
      },
      {
        title: "Nama Lembaga",
        dataIndex: "insitution",
        width: 180,
        render: (value) => value ?? "-",
      },
      {
        title: "Jenis Pelatihan",
        dataIndex: "category",
        width: 140,
        render: (type) => <TrainingTypeTag type={type} />,
      },
      {
        title: "Sumber Biaya",
        dataIndex: "costSources",
        width: 180,
        render: (val) => COST_SOURCE_LABELS[val],
      },
      {
        title: "Tanggal Pengajuan",
        dataIndex: "submissionDate",
        width: 150,
        render: (val) => (
          <Typography.Text style={{ fontSize: "13px", fontStyle: "italic" }}>
            {val ? format(new Date(val), "dd/MM/yyyy") : "-"}
          </Typography.Text>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        width: 150,
        render: (val) => <StatusTag status={val} />,
      },
      {
        title: "Aksi",
        width: 60,
        align: "center",
        render: (record) => (
          <Button
            icon={<Icon icon="solar:eye-bold-duotone" width="18" height="18" />}
            color="default"
            variant="filled"
            onClick={() =>
              navigate(`/training-catalog/pending/detail/${record.uuid}`)
            }
          />
        ),
      },
    ],
    [page],
  );

  const vocationalMetricsDynamic = useMemo(() => {
    return [
      {
        key: "total",
        title: "Lembaga yang Terdaftar",
        count: data?.data?.count?.total || 0,
        icon: <VocationalDashboardIcon width={44} height={44} />,
        backgroundColor: "#dba63d",
        description: "Lembaga dengan data legalitas yang telah terverifikasi.",
        isPrimary: true,
      },
      {
        key: "certified",
        title: "Jumlah Lembaga yang Terverifikasi",
        count: data?.data?.count.certified || 0,
        icon: <StateVocationalDashboardIcon width={44} height={44} />,
        backgroundColor: pallete.grey[100],
        description:
          "Lembaga dengan data legalitas, fasilitas, dan program yang telah terverifikasi.",
        isPrimary: false,
      },
    ];
  }, [filteredVocationalData]);

  const trainingCatalogMetricsDynamic = useMemo(() => {
    return [
      {
        key: "total",
        title: "Total Pelatihan Terdaftar",
        count: trainingCatalogData?.data?.count?.total ?? 0,
        icon: <VocationalDashboardIcon width={44} height={44} />,
        backgroundColor: "#dba63d",
        description: "Jumlah Pelatihan yang Terdaftar.",
        isPrimary: true,
      },
      {
        key: "cpmi",
        title: "Pelatihan Sumber Pendanaan Lainnya",
        count: trainingCatalogData?.data?.count?.pmi ?? 0,
        icon: <CPMIDashboardIcon width={44} height={44} />,
        backgroundColor: pallete.grey[100],
        description:
          "Jumlah Pelatihan dengan Sumber Pendanaan Lainnya.",
        isPrimary: false,
      },
      {
        key: "government",
        title: "Pelatihan Ditanggung APBN",
        count: trainingCatalogData?.data?.count?.gov ?? 0,
        icon: <GovernmentDashboardIcon width={44} height={44} />,
        backgroundColor: pallete.grey[100],
        description:
          "Jumlah Pelatihan dengan Sumber Biaya yang Ditanggung oleh APBN.",
        isPrimary: false,
      },
      {
        key: "employer",
        title: "Pelatihan Ditanggung APBD",
        count: trainingCatalogData?.data?.count?.emp ?? 0,
        icon: <EmployerDashboardIcon width={44} height={44} />,
        backgroundColor: pallete.grey[100],
        description:
          "Jumlah Pelatihan dengan Sumber Biaya yang Ditanggung oleh APBD.",
        isPrimary: false,
      },
    ];
  }, [filteredVocationalData]);

  const metricsData = (() => {
    if (isSuperAdmin) {
      return [...vocationalMetricsDynamic, ...trainingCatalogMetricsDynamic];
    }

    if (isVocationalDirectorate) {
      return vocationalMetricsDynamic;
    }

    if (isCapacityDirectorate) {
      return trainingCatalogMetricsDynamic;
    }

    return [];
  })();

  const MetricsSection = ({ title, data, columns }) => (
    <Card title={title} style={{ marginBottom: 20 }}>
      <Row gutter={[16, 16]}>
        {data.map(
          ({
            key,
            title,
            count,
            icon,
            backgroundColor,
            description,
            isPrimary,
          }) => (
            <Col key={key} xs={24} sm={12} md={columns}>
              <Card
                style={{
                  backgroundColor,
                  color: isPrimary ? "#FFF" : "#000",
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                }}
              >
                <div>{icon}</div>

                <div>
                  <div
                    style={{
                      fontSize: 28,
                      fontWeight: 600,
                      marginTop: 5,
                    }}
                  >
                    {count}
                  </div>

                  <div
                    style={{
                      fontWeight: 600,
                      marginTop: 12,
                      fontSize: 16,
                    }}
                  >
                    {title}
                  </div>

                  <div
                    style={{
                      fontSize: 14,
                      marginTop: 4,
                      fontStyle: "italic",
                      color: isPrimary ? "#eee" : "#A9A9A9",
                    }}
                  >
                    {description}
                  </div>
                </div>
              </Card>
            </Col>
          ),
        )}
      </Row>
    </Card>
  );

  const columnsVocational = [
    {
      title: "No",
      align: "center",
      width: 50,
      fixed: breakpoint.md && "left",
      render: (text, record, index) => index + 1 + (page - 1) * pageSize,
    },
    {
      title: "ID & Nama Lembaga",
      dataIndex: "institutionId",
      width: 250,
      fixed: breakpoint.md && "left",
      render: (_, record) => (
        <>
          <Typography.Text strong>{record.name ?? "-"}</Typography.Text>
          <br />
          <Typography.Text
            type="secondary"
            style={{ fontSize: 13, fontStyle: "italic" }}
          >
            {record.institutionId ?? "-"}
          </Typography.Text>
        </>
      ),
    },
    {
      title: "Jenis Lembaga",
      dataIndex: "type",
      width: 150,
    },
    {
      title: "Bentuk Lembaga",
      dataIndex: "institutionForm",
      width: 250,
    },
    {
      title: "Provinsi",
      dataIndex: "province",
      width: 250,
    },
    {
      title: "Status Legalitas",
      dataIndex: "legalityStatus",
      width: 150,
      render: (val) => <StatusTag status={val} />,
    },
    {
      title: "Status Fasilitas",
      dataIndex: "facilityStatus",
      width: 150,
      render: (val) => <StatusTag status={val} />,
    },
    {
      title: "Status Program",
      dataIndex: "programStatus",
      width: 150,
      render: (val) => <StatusTag status={val} />,
    },
    {
      title: "Update By",
      width: 150,
      render: (val) => <UpdatedByCell value={val} />,
    },
    {
      className: "last-cell-p",
      title: "Aksi",
      align: "right",
      width: 80,
      fixed: "right",
      render: (record) => (
        <Button
          icon={<Icon icon="solar:eye-bold-duotone" width="18" height="18" />}
          color="default"
          variant="filled"
          onClick={() =>
            navigate(`/institutional-verification/detail/${record.uuid}`)
          }
        />
      ),
    },
  ];

  return (
    <>
      {/* Judul dan Metrik */}
      {isSuperAdmin ? (
        <>
          <MetricsSection
            title="Metrik Lembaga Vokasi"
            data={vocationalMetricsDynamic}
            columns={12}
          />

          <MetricsSection
            title="Metrik Katalog Pelatihan"
            data={trainingCatalogMetricsDynamic}
            columns={6}
          />
        </>
      ) : (
        <MetricsSection
          title={
            isVocationalDirectorate
              ? "Metrik Lembaga Vokasi"
              : "Metrik Katalog Pelatihan"
          }
          data={
            isVocationalDirectorate
              ? vocationalMetricsDynamic
              : trainingCatalogMetricsDynamic
          }
          columns={isVocationalDirectorate ? 12 : 6}
        />
      )}

      <RenderIf when={isVocationalDirectorate}>
        <Card title="Lembaga Terbaru">
          <Table
            columns={columnsVocational}
            dataSource={filteredVocationalData}
            pagination={false}
            size="middle"
            rowKey="uuid"
            scroll={{ x: 900 }}
          />
        </Card>
      </RenderIf>

      {/* Tabel Katalog Pelatihan */}
      <RenderIf
        when={
          directorate == "Direktorat Peningkatan Kapasitas" ||
          directorate == "*"
        }
      >
        <Card title="Katalog Pelatihan Terbaru">
          <Table
            columns={columns}
            dataSource={filteredVocationalData}
            pagination={false}
            size="middle"
            rowKey="uuid"
            scroll={{ x: 900 }}
          />
        </Card>
      </RenderIf>
    </>
  );
}
